import { Flex, HStack, Icon, IconButton, Spinner, Text } from '@chakra-ui/react';
import { ManageableDocument } from '@pelicargo/types';
import { modelUtils } from '@pelicargo/utils';
import { MouseEvent } from 'react';
import { HiOutlineDocument, HiXMark } from 'react-icons/hi2';

import { useDocumentViewer } from '../../hooks/documents/useDocumentViewer';

type ChipDocument = Partial<ManageableDocument> & { original_filename?: string | null };

type Props = {
  document: ChipDocument;
  fallbackLabel?: string;
  removeAriaLabel?: string;
  onNameClick?: () => void;
  isNameLoading?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
};

export const DocumentChip = ({
  document,
  fallbackLabel = 'Document',
  removeAriaLabel = 'Remove document',
  onNameClick,
  isNameLoading,
  onDelete,
  isDeleting,
}: Props) => {
  const viewer = useDocumentViewer();

  const nameProps = onNameClick
    ? { cursor: 'pointer', title: 'Download', _hover: { textDecoration: 'underline' }, onClick: onNameClick }
    : {};

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    onDelete?.();
  };

  return (
    <Flex align="center" justify="space-between" gap="2" flex="1" minW="0">
      <HStack spacing="2" minW={0} flex="1" {...nameProps}>
        {isNameLoading ? (
          <Spinner size="xs" color="text.muted" />
        ) : (
          <Icon as={HiOutlineDocument} color="text.muted" boxSize="4" flexShrink={0} />
        )}
        <Text fontSize="sm" color="text.paragraph" noOfLines={1} title={document?.original_filename ?? undefined}>
          {document?.original_filename || fallbackLabel}
        </Text>
      </HStack>
      {!!onDelete && modelUtils.document.canManage(document, viewer) && (
        <IconButton
          aria-label={removeAriaLabel}
          icon={<HiXMark />}
          size="xs"
          variant="ghost"
          color="text.muted"
          flexShrink={0}
          isLoading={isDeleting}
          onClick={handleDelete}
        />
      )}
    </Flex>
  );
};
